import { useEffect, useState, type MouseEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import { AlertTriangle } from 'lucide-react';
import { formatTime, toBerlinDateKey } from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import { Icon } from '../../ds/components';
import type { CalendarWeek, WeekDay, WeekItem, WeekTone } from './calendarApi';
import { addDays } from './dates';
import styles from './WeekGrid.module.css';

/** One hour of the grid is 36 px (Beratung 01: hours 7–20, 504 px). */
const HOUR_PX = 36;
const MINUTE_PX = HOUR_PX / 60;
/** Phone tiles are one line, 16 px high (A2). */
const PHONE_HEIGHT = 16;
/** Below this height a tile uses the compact padding (3/8) and a 10 px subtitle. */
const COMPACT_HEIGHT = 30;
/** Below this height a tile shows its first line only. */
const TINY_HEIGHT = 22;

/** Minutes since midnight (Europe/Berlin) of a point in time. */
function berlinMinutes(value: string | Date): number {
  const [hours = 0, minutes = 0] = formatTime(value).split(':').map(Number);
  return hours * 60 + minutes;
}

interface Placed {
  item: WeekItem;
  start: number;
  end: number;
  column: number;
  columns: number;
}

/**
 * The item parts that fall on `day` (minutes from midnight, clamped to the grid's hours) with side-by-side
 * columns for overlapping tiles — two overlapping tiles take the halves 48/48 of the frame (right:52 % /
 * left:52 %).
 */
export function placeItems(items: WeekItem[], day: string, from: number, to: number): Placed[] {
  const next = addDays(day, 1);
  const parts: Placed[] = [];
  for (const item of items) {
    const startDay = toBerlinDateKey(item.startsAt);
    const endDay = toBerlinDateKey(new Date(new Date(item.endsAt).getTime() - 1));
    if (startDay > day || endDay < day) continue;
    const start = startDay === day ? berlinMinutes(item.startsAt) : 0;
    const end = endDay === day && toBerlinDateKey(item.endsAt) !== next ? berlinMinutes(item.endsAt) : 1440;
    const clampedStart = Math.max(start, from * 60);
    const clampedEnd = Math.min(end, to * 60);
    if (clampedEnd <= clampedStart) continue;
    parts.push({ item, start: clampedStart, end: clampedEnd, column: 0, columns: 1 });
  }
  parts.sort((a, b) => a.start - b.start || b.end - a.end);
  let cluster: Placed[] = [];
  let clusterEnd = -1;
  const flush = () => {
    const columnEnds: number[] = [];
    for (const part of cluster) {
      const free = columnEnds.findIndex((end) => end <= part.start);
      const column = free === -1 ? columnEnds.length : free;
      columnEnds[column] = part.end;
      part.column = column;
    }
    for (const part of cluster) part.columns = columnEnds.length;
    cluster = [];
  };
  for (const part of parts) {
    if (cluster.length > 0 && part.start >= clusterEnd) flush();
    cluster.push(part);
    clusterEnd = Math.max(clusterEnd, part.end);
  }
  flush();
  return parts;
}

const toneClass: Record<WeekTone, string | undefined> = {
  video: styles.toneVideo,
  on_site: styles.toneOnSite,
  notary: styles.toneNotary,
  phone: styles.tonePhone,
  internal: styles.toneInternal,
  conflict: styles.toneConflict,
};

function horizontal(column: number, columns: number): { left: string; right: string } {
  if (columns === 1) return { left: '3px', right: '3px' };
  if (columns === 2) return column === 0 ? { left: '3px', right: '52%' } : { left: '52%', right: '3px' };
  const width = 100 / columns;
  return { left: `calc(${column * width}% + 2px)`, right: `calc(${100 - (column + 1) * width}% + 2px)` };
}

/** „14." / „Mi · heute" from the API label „Mi · heute 16." */
export function splitDayLabel(label: string): { eyebrow: string; number: string } {
  const index = label.lastIndexOf(' ');
  return index === -1
    ? { eyebrow: label, number: '' }
    : { eyebrow: label.slice(0, index), number: label.slice(index + 1) };
}

/** The current time, updated every minute (the „Jetzt" line). */
export function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

function Tile({ part }: { part: Placed }) {
  const { item } = part;
  const split = part.columns > 1;
  const phone = item.tone === 'phone' && !split;
  // `start` counts from the grid's first hour (DayColumn shifts the parts).
  const top = part.start * MINUTE_PX;
  const height = phone
    ? PHONE_HEIGHT
    : Math.max(PHONE_HEIGHT, Math.round((part.end - part.start) * MINUTE_PX) - 2);
  // Short tiles (under 22 px, e.g. 30 minutes) keep one line like the phone tiles.
  const tiny = !phone && height < TINY_HEIGHT;
  const compact = !phone && !split && !tiny && height < COMPACT_HEIGHT;
  const { left, right } = horizontal(part.column, part.columns);
  // A halved tile shows the time on its first line and the kind on the second (Beratung 01 „16:00 / Videoberatung").
  const match = /^(\d{2}:\d{2})\s+(.+)$/.exec(item.title);
  const lines: [string, string | null] =
    split && match && item.tone !== 'conflict'
      ? [match[1] ?? item.title, match[2] ?? null]
      : [item.title, item.subtitle];
  const fullLabel = [item.title, item.subtitle].filter(Boolean).join(' · ');
  const className = clsx(
    styles.tile,
    toneClass[item.tone],
    item.running && styles.running,
    phone && styles.phone,
    compact && styles.compact,
    tiny && styles.tiny,
    split && styles.split,
  );
  const style = { top, height, left, right };
  const content: ReactNode = phone ? (
    <span className={styles.line}>{item.title}</span>
  ) : (
    <>
      <span className={styles.title}>
        {item.running ? <span className={styles.liveDot} aria-hidden="true" /> : null}
        {lines[0]}
        {item.tone === 'conflict' ? (
          <Icon icon={AlertTriangle} size={16} className={styles.warnIcon} />
        ) : null}
      </span>
      {lines[1] && !tiny ? <span className={styles.subtitle}>{lines[1]}</span> : null}
    </>
  );
  const link = item.clickable && item.href ? item.href : null;
  return (
    <div
      className={clsx(className, link ? styles.clickable : styles.inert)}
      style={style}
      title={fullLabel}
      aria-disabled={link ? undefined : true}
    >
      {content}
      {link ? <Link to={link} className={styles.tileLink} aria-label={fullLabel} /> : null}
    </div>
  );
}

interface DayColumnProps {
  day: WeekDay;
  items: WeekItem[];
  from: number;
  to: number;
  now: Date;
  onPickTime?: ((day: string, minutes: number) => void) | undefined;
}

function DayColumn({ day, items, from, to, now, onPickTime }: DayColumnProps) {
  const parts = placeItems(items, day.date, from, to).map((part) => ({
    ...part,
    start: part.start - from * 60,
    end: part.end - from * 60,
  }));
  const nowMinutes = day.today ? berlinMinutes(now) - from * 60 : -1;
  const showNow = day.today && nowMinutes >= 0 && nowMinutes <= (to - from) * 60;
  function pick(event: MouseEvent<HTMLDivElement>) {
    if (!onPickTime || event.target !== event.currentTarget) return;
    const offset = event.nativeEvent.offsetY;
    const minutes = from * 60 + Math.floor(offset / MINUTE_PX / 15) * 15;
    onPickTime(day.date, minutes);
  }
  return (
    <div
      className={clsx(
        styles.column,
        day.today && styles.columnToday,
        day.unavailable && styles.columnUnavailable,
        onPickTime && styles.columnPickable,
      )}
      style={{ height: (to - from) * HOUR_PX }}
      onClick={pick}
    >
      {parts.map((part) => (
        <Tile key={`${part.item.kind}-${part.item.id}`} part={part} />
      ))}
      {showNow ? (
        <>
          <span className={styles.nowLine} style={{ top: nowMinutes * MINUTE_PX }} aria-hidden="true" />
          <span className={styles.nowDot} style={{ top: nowMinutes * MINUTE_PX - 4 }} aria-hidden="true" />
        </>
      ) : null}
    </div>
  );
}

export interface WeekGridProps {
  week: CalendarWeek;
  /** The days to draw (all seven by default; the mobile view draws one). */
  days?: WeekDay[] | undefined;
  /** Click on an empty part of a day column → the time (minutes from midnight, 15-minute steps). */
  onPickTime?: ((day: string, minutes: number) => void) | undefined;
  className?: string | undefined;
}

/** Beratung 01 — the week grid: day heads, hours 7–20 in 36 px rows, the tiles, the „Jetzt" line. */
export function WeekGrid({ week, days = week.days, onPickTime, className }: WeekGridProps) {
  const now = useNow();
  const { from, to } = week.hours;
  const hours = Array.from({ length: to - from }, (_, index) => from + index);
  const columns = { gridTemplateColumns: `54px repeat(${days.length}, minmax(0, 1fr))` };
  return (
    <div className={clsx(styles.grid, className)}>
      <div className={styles.headRow} style={columns}>
        <div />
        {days.map((day) => {
          const label = splitDayLabel(day.label);
          return (
            <div key={day.date} className={clsx(styles.dayHead, day.today && styles.dayHeadToday)}>
              <div className={styles.dayEyebrow}>{label.eyebrow}</div>
              <div className={clsx(styles.dayNumber, day.unavailable && styles.dayNumberMuted)}>
                {label.number}
              </div>
            </div>
          );
        })}
      </div>
      <div className={styles.body} style={columns}>
        <div className={styles.hours} style={{ height: (to - from) * HOUR_PX }} aria-hidden="true">
          {hours.map((hour, index) => (
            <span key={hour} className={styles.hour} style={{ top: index * HOUR_PX - 7 }}>
              {hour}
            </span>
          ))}
        </div>
        {days.map((day) => (
          <DayColumn
            key={day.date}
            day={day}
            items={week.items}
            from={from}
            to={to}
            now={now}
            onPickTime={onPickTime}
          />
        ))}
      </div>
    </div>
  );
}

/** The legend under the grid (Beratung 01). */
export function WeekLegend() {
  return (
    <div className={styles.legend}>
      <span className={styles.legendItem}>
        <span className={clsx(styles.swatch, styles.toneVideo)} />
        Videoberatung
      </span>
      <span className={styles.legendItem}>
        <span className={clsx(styles.swatch, styles.toneOnSite)} />
        Besichtigung vor Ort
      </span>
      <span className={styles.legendItem}>
        <span className={clsx(styles.swatch, styles.toneNotary)} />
        Notartermin
      </span>
      <span className={styles.legendItem}>
        <span className={clsx(styles.swatch, styles.tonePhone)} />
        Telefonat
      </span>
      <span className={styles.legendItem}>
        <span className={clsx(styles.swatch, styles.toneInternal)} />
        Intern · nicht verfügbar
      </span>
      <span className={styles.legendHint}>
        Belegte Zeiten aus verbundenem Kalender werden automatisch ausgeschnitten.
      </span>
    </div>
  );
}
